import { PriceDisplay } from '../PriceDisplay';
import { Spinner } from '../Spinner';
import {
  Content,
  MetaItem,
  MetaList,
  PriceColumn,
  RemoveButton,
  RemoveRow,
  RemoveStatus,
  SubPriceRow,
  TextColumn,
  Thumbnail,
  ThumbnailImage,
  ThumbnailPlaceholder,
  Title,
  TopRow,
  TopRowLink,
  Wrapper,
} from './BasketCard.styles';
import type { BasketCardProps } from './BasketCard.types';

export function BasketCard({
  title,
  href,
  imageUrl,
  imageAlt,
  meta,
  extras,
  price,
  subPrice,
  subPriceSuffix,
  locale,
  noImageLabel = 'No image',
  onRemove,
  removeLabel = 'Remove',
  removing = false,
  removeDisabled = false,
  removingLabel = 'Removing…',
}: BasketCardProps) {
  const detailLines = [...(meta ?? []), ...(extras ?? [])];

  const body = (
    <>
      <Thumbnail>
        {imageUrl ? (
          <ThumbnailImage src={imageUrl} alt={imageAlt ?? title} />
        ) : (
          <ThumbnailPlaceholder aria-hidden="true">{noImageLabel}</ThumbnailPlaceholder>
        )}
      </Thumbnail>

      <Content>
        <TextColumn>
          <Title className="basket-card-title">{title}</Title>
          {detailLines.length > 0 && (
            <MetaList>
              {detailLines.map((line, index) => (
                <MetaItem key={`${line}-${index}`}>{line}</MetaItem>
              ))}
            </MetaList>
          )}
        </TextColumn>

        <PriceColumn>
          <PriceDisplay
            amount={price.amount}
            currencyCode={price.currencyCode}
            locale={locale}
            size="base"
            weight="medium"
          />
          {subPrice && (
            <SubPriceRow>
              <PriceDisplay
                amount={subPrice.amount}
                currencyCode={subPrice.currencyCode}
                locale={locale}
                size="small"
                weight="normal"
              />
              {subPriceSuffix ? ` ${subPriceSuffix}` : ''}
            </SubPriceRow>
          )}
        </PriceColumn>
      </Content>
    </>
  );

  return (
    <Wrapper data-testid="basket-card" data-removing={removing || undefined}>
      {href ? <TopRowLink href={href}>{body}</TopRowLink> : <TopRow>{body}</TopRow>}

      {onRemove && (
        <RemoveRow data-remove-row="true">
          {removing ? (
            <RemoveStatus role="status">
              <Spinner tone="inherit" aria-hidden />
              {removingLabel}
            </RemoveStatus>
          ) : (
            <RemoveButton type="button" onClick={onRemove} disabled={removeDisabled}>
              {removeLabel}
            </RemoveButton>
          )}
        </RemoveRow>
      )}
    </Wrapper>
  );
}
